/**
 * StudioToolPicker
 * Animated popup that appears above the Plus button on the Canvas page.
 * Lists all studio tools as circular icon buttons in a 2-column grid.
 */
import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Loader2 } from "lucide-react";
import { STUDIO_CARDS } from "@/lib/studio-tools";

interface StudioToolPickerProps {
    isOpen: boolean;
    onClose: () => void;
    onToolClick: (toolSlug: string | null, label: string) => void;
    loadingTool: string | null;
}

export function StudioToolPicker({
    isOpen,
    onClose,
    onToolClick,
    loadingTool,
}: StudioToolPickerProps) {
    const pickerRef = useRef<HTMLDivElement>(null);

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleClick = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        // slight delay so the open-click doesn't immediately close
        const timer = setTimeout(() => document.addEventListener("mousedown", handleClick), 50);
        return () => {
            clearTimeout(timer);
            document.removeEventListener("mousedown", handleClick);
        };
    }, [isOpen, onClose]);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    ref={pickerRef}
                    initial={{ opacity: 0, scale: 0.9, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 10 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                    // Position is handled by parent (absolute, bottom-full above the bar)
                    className="absolute bottom-full mb-3 -left-[110px] -translate-x-1/2 z-30
                     bg-bg-surface border border-border-subtle rounded-xl shadow-popover
                     p-4 w-[420px]"
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wider mb-3 px-1">
                        Studio Tools
                    </p>

                    {/* 2-column grid of circular tool buttons */}
                    <div className="grid grid-cols-2 gap-2">
                        {STUDIO_CARDS.map((card) => {
                            const Icon = card.icon;
                            const isLoading = !!card.toolSlug && loadingTool === card.toolSlug;
                            const isDisabled = !card.toolSlug || (loadingTool !== null && !isLoading);

                            return (
                                <button
                                    key={card.id}
                                    disabled={isDisabled}
                                    onClick={() => {
                                        if (!isDisabled && !isLoading) {
                                            onToolClick(card.toolSlug, card.label);
                                        }
                                    }}
                                    className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all
                    ${isDisabled
                                            ? "cursor-not-allowed"
                                            : "hover:bg-bg-canvas cursor-pointer hover:scale-[1.02]"
                                        }
                  `}
                                >
                                    {/* Neutral icon tile (gray when disabled) */}
                                    <div
                                        className={`
                      w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                      ${isDisabled
                                                ? "bg-action-disabled-bg text-action-disabled-fg"
                                                : `text-text-primary ${card.color}`
                                            }
                    `}
                                    >
                                        {isLoading ? (
                                            <Loader2 size={16} className="text-text-primary animate-spin" />
                                        ) : (
                                            <Icon color="currentColor" />
                                        )}
                                    </div>

                                    {/* Label */}
                                    <span
                                        className={`text-xs font-medium leading-tight ${isDisabled ? "text-action-disabled-fg" : "text-text-primary"
                                            }`}
                                    >
                                        {card.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
